import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sub } from "date-fns";
import { Program } from "../../API";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser, selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import { returnIdsOfProgramsThatUserAdmins } from "../../app/utils";
import { alphabetizePrograms, useAllPrograms } from "../Programs/programUtils";
import { selectProgramsStatus } from "../Programs/programsSlice";
import { ReportsDateRangePicker } from "../Reports/ReportsDateRangePicker";
import {
  ActivityFilterOption,
  ProgramMemberFilterOption,
  fetchProgramAllowsTraineeEvaluators,
  fetchProgramMembersForAssessmentFilters,
  mergeDistinctProgramMemberOptions,
  searchActivitiesForAssessmentFilter
} from "./programAssessmentLookupAPI";
import {
  DeletedFilter,
  formatProgramAssessmentRemovedBy,
  programAssessmentDeletionNoteForDisplay,
  programAssessmentDeletionReasonForDisplay,
  programAssessmentHasDeletionAuditMetadata,
  ProgramAssessmentListItem,
  ProgramAssessmentsListRequest,
  fetchProgramAssessmentsPageSafe
} from "./programAssessmentsAPI";
import { formatIsoDisplay } from "./programAssessmentsDisplay";
import { ProgramAssessmentDetailDrawer } from "./ProgramAssessmentDetailDrawer";

function firstNetworkSponsorId(program: Program | undefined): string | undefined {
  const items = program?.networks?.items;
  if (!items?.length) {
    return undefined;
  }
  for (const np of items) {
    const id = np?.network?.sponsorId;
    if (id) {
      return id;
    }
  }
  return undefined;
}

const pageDetails = {
  className: "ProgramAssessmentsPage",
  headerText: "Program Assessments",
  headerVariant: "h2" as const
};

/** Rows per page for program assessments list API (fixed unless we add UI to change it). */
const PROGRAM_ASSESSMENTS_PAGE_SIZE = 50;

type ProgramAssessmentsResultsSummaryState = {
  loading: boolean;
  itemCount: number;
  grandTotalIsConsistent: boolean;
  hasMore: boolean;
  totalCount: number | null;
  totalCountCapped: boolean;
};

/** Summary line above the program assessments table (loading, totals, load-more). */
function programAssessmentsResultsSummaryText(
  t: TFunction,
  state: ProgramAssessmentsResultsSummaryState
): string {
  const {
    loading,
    itemCount,
    grandTotalIsConsistent,
    hasMore,
    totalCount,
    totalCountCapped
  } = state;

  if (loading && itemCount === 0) {
    return t("Searching…");
  }
  if (loading && itemCount > 0) {
    return t("Updating results…");
  }

  if (grandTotalIsConsistent && totalCount !== null) {
    if (totalCountCapped) {
      return t("Showing {{loaded}} of more than {{total}}", {
        loaded: itemCount,
        total: totalCount
      });
    }
    return t("Showing {{loaded}} of {{total}}", {
      loaded: itemCount,
      total: totalCount
    });
  }

  if (hasMore) {
    return t("Showing {{loaded}} rows", { loaded: itemCount });
  }

  return t("Showing {{count}} row(s)", { count: itemCount });
}

export const ProgramAssessmentsPage = () => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const programsLoadingStatus = useAppSelector(selectProgramsStatus);
  const currentUserPermissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = currentUserPermissions?.includes(CognitoUserGroups.SysAdmins);
  const isProgramAdmin = currentUserPermissions?.includes(
    CognitoUserGroups.ProgramAdmins
  );

  const { programs } = useAllPrograms();
  const idsOfProgramsThatUserAdmins = returnIdsOfProgramsThatUserAdmins(currentUser);
  const programsThatUserAdmins = programs?.filter((program) =>
    idsOfProgramsThatUserAdmins.includes(program.id)
  );
  const programsToDisplay =
    isSysAdmin ? programs : isProgramAdmin ? programsThatUserAdmins : [];
  const sortedPrograms = useMemo(
    () => (programsToDisplay ? alphabetizePrograms(programsToDisplay, true) : []),
    [programsToDisplay]
  );

  const [programId, setProgramId] = useState<string>("");
  const selectedProgram = useMemo(
    () => sortedPrograms.find((p) => p.id === programId),
    [sortedPrograms, programId]
  );
  const activitySponsorId = firstNetworkSponsorId(selectedProgram);

  const [fromDate, setFromDate] = useState<Date | null>(sub(new Date(), { months: 1 }));
  const [toDate, setToDate] = useState<Date | null>(new Date());

  const [traineeOptions, setTraineeOptions] = useState<ProgramMemberFilterOption[]>([]);
  const [attendingOptions, setAttendingOptions] = useState<ProgramMemberFilterOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  const [selectedTrainee, setSelectedTrainee] = useState<ProgramMemberFilterOption | null>(
    null
  );
  const [selectedAttending, setSelectedAttending] = useState<ProgramMemberFilterOption | null>(
    null
  );

  const [activityInput, setActivityInput] = useState("");
  const [activityOptions, setActivityOptions] = useState<ActivityFilterOption[]>([]);
  const [activitySearchLoading, setActivitySearchLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityFilterOption | null>(null);

  const [completionFromDate, setCompletionFromDate] = useState<Date | null>(null);
  const [completionToDate, setCompletionToDate] = useState<Date | null>(null);
  const [deletedFilter, setDeletedFilter] = useState<DeletedFilter>("exclude_deleted");

  const [items, setItems] = useState<ProgramAssessmentListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  /** Avoid showing “no results” after the user dismisses an API error alert. */
  const [lastListRequestSucceeded, setLastListRequestSucceeded] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState<ProgramAssessmentListItem | null>(null);
  /** True when the API stopped counting at TOTAL_MATCH_COUNT_CAP (at least that many matches). */
  const [totalCountCapped, setTotalCountCapped] = useState(false);

  /** Bumped for list resets and new requests so only the latest response can update UI state. */
  const listRequestEpochRef = useRef(0);

  useEffect(() => {
    if (sortedPrograms.length !== 1) {
      return;
    }
    const onlyId = sortedPrograms[0].id;
    setProgramId((previous) => (previous === onlyId ? previous : onlyId));
  }, [sortedPrograms]);

  useEffect(() => {
    listRequestEpochRef.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setTotalCount(null);
    setTotalCountCapped(false);
    setSearched(false);
    setLastListRequestSucceeded(false);
    setError(null);
    setLoading(false);
    setLoadingMore(false);
    setDrawerOpen(false);
    setDrawerRow(null);
  }, [
    programId,
    fromDate,
    toDate,
    deletedFilter,
    selectedTrainee,
    selectedAttending,
    selectedActivity,
    completionFromDate,
    completionToDate
  ]);

  useEffect(() => {
    setSelectedTrainee(null);
    setSelectedAttending(null);
    setSelectedActivity(null);
    setActivityInput("");
    setActivityOptions([]);
    setMembersError(null);
  }, [programId]);

  useEffect(() => {
    if (!programId) {
      setTraineeOptions([]);
      setAttendingOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const [{ trainees, attendings }, traineeEvaluatorsAllowed] = await Promise.all([
          fetchProgramMembersForAssessmentFilters(programId),
          fetchProgramAllowsTraineeEvaluators(programId)
        ]);
        if (!cancelled) {
          setTraineeOptions(trainees);
          setAttendingOptions(
            traineeEvaluatorsAllowed
              ? mergeDistinctProgramMemberOptions(attendings, trainees)
              : attendings
          );
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setTraineeOptions([]);
          setAttendingOptions([]);
          setMembersError(
            (err as Error)?.message || t("Could not load program members for filters.")
          );
        }
      } finally {
        if (!cancelled) {
          setMembersLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId, t]);

  useEffect(() => {
    if (!programId) {
      return;
    }
    const q = activityInput.trim();
    if (q.length < 2) {
      setActivityOptions([]);
      setActivitySearchLoading(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      void (async () => {
        setActivitySearchLoading(true);
        try {
          const opts = await searchActivitiesForAssessmentFilter({
            titleContains: q,
            sponsorId: activitySponsorId
          });
          if (cancelled) {
            return;
          }
          setActivityOptions(opts);
        } catch {
          if (!cancelled) {
            setActivityOptions([]);
          }
        } finally {
          if (!cancelled) {
            setActivitySearchLoading(false);
          }
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [activityInput, programId, activitySponsorId]);

  const buildBody = useCallback(
    (cursor: string | null) => {
      if (!programId || !fromDate || !toDate) return null;
      const body: ProgramAssessmentsListRequest = {
        programId,
        activityEventDateFrom: format(fromDate, "yyyy-MM-dd"),
        activityEventDateTo: format(toDate, "yyyy-MM-dd"),
        deletedFilter,
        limit: PROGRAM_ASSESSMENTS_PAGE_SIZE,
        cursor: cursor || undefined
      };

      // First page only: capped total count (same filters) so we can show "Showing N of M" + measure cost in list lambda logs (`includeTotalCount=true`).
      if (!cursor) {
        body.includeTotalCount = true;
      }

      if (selectedTrainee) {
        body.traineeId = selectedTrainee.userId;
      }
      if (selectedAttending) {
        body.attendingId = selectedAttending.userId;
      }
      if (selectedActivity) {
        body.activityId = selectedActivity.id;
      }

      if (completionFromDate && completionToDate) {
        body.completionDateFrom = format(completionFromDate, "yyyy-MM-dd");
        body.completionDateTo = format(completionToDate, "yyyy-MM-dd");
      }

      return body;
    },
    [
      programId,
      fromDate,
      toDate,
      selectedTrainee,
      selectedAttending,
      selectedActivity,
      completionFromDate,
      completionToDate,
      deletedFilter
    ]
  );

  const runSearch = async (append: boolean) => {
    const requestEpoch = ++listRequestEpochRef.current;
    const cursor = append ? nextCursor : null;
    const body = buildBody(cursor);
    if (!body) {
      setError(t("Select a program and date range."));
      return;
    }
    const completionPartial =
      (completionFromDate && !completionToDate) ||
      (!completionFromDate && completionToDate);
    if (completionPartial) {
      setError(
        t("Enter both completion start and end dates, or clear both, to filter by completion date.")
      );
      return;
    }
    setError(null);
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setSearched(true);
    }
    const result = await fetchProgramAssessmentsPageSafe(body);
    if (listRequestEpochRef.current !== requestEpoch) {
      return;
    }
    if (append) {
      setLoadingMore(false);
    } else {
      setLoading(false);
    }
    if (!result.ok) {
      setLastListRequestSucceeded(false);
      setError(result.message);
      if (!append) {
        setItems([]);
        setNextCursor(null);
        setHasMore(false);
        setTotalCount(null);
        setTotalCountCapped(false);
      }
      return;
    }
    setLastListRequestSucceeded(true);
    const { data } = result;
    setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    setNextCursor(data.nextCursor);
    setHasMore(data.hasMore);
    if (!append) {
      setTotalCount(typeof data.totalCount === "number" ? data.totalCount : null);
      setTotalCountCapped(Boolean(data.totalCountCapped));
    }
  };

  const handleSearch = () => {
    void runSearch(false);
  };
  const handleLoadMore = () => {
    void runSearch(true);
  };

  const handleAssessmentDeleted = (payload: {
    primaryAssessmentEventId: string;
    deletedAt: string;
    deletedReason: string;
    deletedNote: string | null;
  }) => {
    setItems((prev) =>
      prev.map((item) =>
        item.assessmentEventId === payload.primaryAssessmentEventId
          ? {
              ...item,
              deletedAt: payload.deletedAt,
              deletedReason: payload.deletedReason,
              deletedNote: payload.deletedNote,
              deletedByUserId: null,
              deletedByFullName: null
            }
          : item
      )
    );
    void runSearch(false);
  };

  const programSelectionNotif =
    programsLoadingStatus === "loading" ? t("Loading Programs...") : t("Select Program");

  const activitySearchHint = activitySponsorId
    ? t("Search scoped to this program’s sponsor where possible.")
    : t("Search uses activity title; sponsor filter unavailable for this program.");

  /** Real grand total must be ≥ rows shown; avoids “100 of 50” when total is stale or was mistaken for first-page size. */
  const grandTotalIsConsistent =
    totalCount !== null && items.length <= totalCount;

  return (
    <PageWrapper {...pageDetails} noScroll>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          gap: 1.5,
          [theme.breakpoints.up("md")]: {
            height: "calc(100dvh - 80px)",
            maxHeight: "calc(100dvh - 80px)",
            overflow: "hidden"
          }
        })}
      >
      <Card variant="outlined" sx={{ flexShrink: 0, width: "100%" }}>
        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
          {membersError && (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
              onClose={() => setMembersError(null)}
            >
              {membersError}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: 1.25,
              columnGap: 1.5,
              rowGap: 1
            }}
          >
            <FormControl
              required
              sx={{ minWidth: { xs: "100%", sm: 220 }, flex: { sm: "0 1 200px" } }}
              size="small"
            >
              <InputLabel id="pa-program-label">{t("Program")}</InputLabel>
              <Select
                labelId="pa-program-label"
                label={t("Program")}
                value={programId}
                disabled={programsLoadingStatus === "loading"}
                onChange={(e) => setProgramId(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>{programSelectionNotif}</em>
                </MenuItem>
                {sortedPrograms?.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {`${program.institution?.name || "N/A"} → ${program.name || "N/A"}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: { sm: "0 1 280px" }, minWidth: { xs: "100%", sm: 260 } }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                {t("Activity event dates")}
                <Typography component="span" sx={{ color: "error.main", fontWeight: 600 }} aria-hidden>
                  {" *"}
                </Typography>
              </Typography>
              <ReportsDateRangePicker
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                size="small"
              />
            </Box>

            <FormControl sx={{ minWidth: { xs: "100%", sm: 160 }, flex: { sm: "0 1 160px" } }} size="small">
              <InputLabel id="pa-deleted-label">{t("Deleted (optional)")}</InputLabel>
              <Select
                labelId="pa-deleted-label"
                label={t("Deleted (optional)")}
                value={deletedFilter}
                onChange={(e) =>
                  setDeletedFilter(e.target.value as DeletedFilter)
                }
              >
                <MenuItem value="exclude_deleted">
                  {t("Active only (default)")}
                </MenuItem>
                <MenuItem value="deleted_only">
                  {t("Deleted only (audit)")}
                </MenuItem>
                <MenuItem value="all">{t("All")}</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              size="small"
              sx={{ flex: { sm: "1 1 200px" }, minWidth: { xs: "100%", sm: 180 } }}
              options={traineeOptions}
              value={selectedTrainee}
              onChange={(_, value) => setSelectedTrainee(value)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              disabled={!programId || membersLoading}
              loading={membersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Trainee (optional)")}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {membersLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              sx={{ flex: { sm: "1 1 200px" }, minWidth: { xs: "100%", sm: 180 } }}
              options={attendingOptions}
              value={selectedAttending}
              onChange={(_, value) => setSelectedAttending(value)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              disabled={!programId || membersLoading}
              loading={membersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Evaluator (optional)")}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {membersLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />

            <Box sx={{ flex: { sm: "1 1 220px" }, minWidth: { xs: "100%", sm: 200 } }}>
              <Tooltip title={activitySearchHint}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={activityOptions}
                  value={selectedActivity}
                  onChange={(_, value) => {
                    setSelectedActivity(value);
                    if (value) {
                      setActivityInput(value.title);
                    }
                  }}
                  inputValue={activityInput}
                  onInputChange={(_, value, reason) => {
                    setActivityInput(value);
                    if (reason === "clear") {
                      setSelectedActivity(null);
                      setActivityOptions([]);
                    }
                  }}
                  filterOptions={(opts) => opts}
                  getOptionLabel={(o) => o.title}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={!programId}
                  loading={activitySearchLoading}
                  noOptionsText={
                    activityInput.trim().length < 2
                      ? t("Type at least 2 characters to search activities")
                      : t("No activities found")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Activity (optional)")}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {activitySearchLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Tooltip>
            </Box>

            <Box sx={{ flex: { sm: "1 1 280px" }, minWidth: { xs: "100%", sm: 260 } }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                {t("Completion (optional)")}
              </Typography>
              <ReportsDateRangePicker
                fromDate={completionFromDate}
                setFromDate={setCompletionFromDate}
                toDate={completionToDate}
                setToDate={setCompletionToDate}
                size="small"
              />
            </Box>

            <Box
              aria-hidden
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 0" },
                minWidth: { sm: 8 },
                minHeight: 0
              }}
            />

            <Button
              type="button"
              variant="contained"
              size="medium"
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "stretch", sm: "flex-end" }
              }}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              disabled={loading || !programId || !fromDate || !toDate}
              onClick={handleSearch}
            >
              {t("Search")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {searched && (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            width: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            [theme.breakpoints.down("md")]: {
              flex: "none",
              minHeight: "min(55vh, 480px)",
              maxHeight: "70vh"
            }
          })}
        >
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "action.hover"
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography variant="body2" color="text.secondary">
                {programAssessmentsResultsSummaryText(t, {
                  loading,
                  itemCount: items.length,
                  grandTotalIsConsistent,
                  hasMore,
                  totalCount,
                  totalCountCapped
                })}
              </Typography>
              {!loading && items.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t("Click a row to view details and removal options.")}
                </Typography>
              ) : null}
            </Box>
            {hasMore && !loading && (
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleLoadMore}
                disabled={loadingMore}
                startIcon={
                  loadingMore ? <CircularProgress size={18} /> : undefined
                }
              >
                {t("Load more")}
              </Button>
            )}
          </Box>
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", width: "100%" }}>
            {loading && items.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                  p: 3
                }}
              >
                <CircularProgress />
              </Box>
            ) : !loading && items.length === 0 && !error && lastListRequestSucceeded ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("No assessments matched your filters.")}
                </Typography>
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ whiteSpace: "nowrap", maxWidth: 160 }}
                      title={t(
                        "When the activity event occurred (shown in your local timezone)."
                      )}
                    >
                      {t("Activity event date")}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {t("Completed")}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        width: "18%",
                        minWidth: 120
                      }}
                    >
                      {t("Activity")}
                    </TableCell>
                    <TableCell>{t("Trainee")}</TableCell>
                    <TableCell>{t("Evaluator")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Removed by")}</TableCell>
                    <TableCell>{t("Deletion reason")}</TableCell>
                    <TableCell>{t("Deletion note")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => {
                    const openAssessmentDetail = () => {
                      setDrawerRow(row);
                      setDrawerOpen(true);
                    };
                    return (
                    <TableRow
                      key={row.assessmentEventId}
                      hover
                      tabIndex={0}
                      role="button"
                      aria-label={
                        t("Open assessment details, ID {{id}}", {
                          id: row.assessmentEventId
                        }) as string
                      }
                      sx={{
                        cursor: "pointer",
                        "&:focus-visible": {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2
                        }
                      }}
                      title={
                        t("Assessment event ID: {{id}}", {
                          id: row.assessmentEventId
                        }) as string
                      }
                      onClick={openAssessmentDetail}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openAssessmentDetail();
                        }
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        {formatIsoDisplay(row.activityEventDate)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        {formatIsoDisplay(row.completionDate ?? undefined)}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          verticalAlign: "top"
                        }}
                      >
                        {row.activity?.title || row.activity?.id || "—"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {row.subjectFullName || row.subjectUserId || "—"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {row.raterFullName || row.raterUserId || "—"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {row.deletedAt ? (
                          <Chip
                            label={t("Deleted")}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label={t("Active")}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {programAssessmentHasDeletionAuditMetadata(row)
                          ? formatProgramAssessmentRemovedBy(
                              row.deletedByFullName,
                              row.deletedByUserId,
                              t
                            )
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {programAssessmentHasDeletionAuditMetadata(row)
                          ? programAssessmentDeletionReasonForDisplay(
                              row.deletedReason,
                              row.deletedNote
                            )
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: "top",
                          maxWidth: 280,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word"
                        }}
                      >
                        {programAssessmentHasDeletionAuditMetadata(row)
                          ? programAssessmentDeletionNoteForDisplay(
                              row.deletedReason,
                              row.deletedNote
                            )
                          : "—"}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      )}
      </Box>

      <ProgramAssessmentDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerRow(null);
        }}
        listRow={drawerRow}
        onDeleted={handleAssessmentDeleted}
      />
    </PageWrapper>
  );
};
